import React, { useEffect, useMemo, useState, useCallback } from "react";
import Loader from "../../components/Loader.tsx";
import Layout from "../../components/Layout.tsx";
import DocumentsViewer from "../../components/profile/DocumentsViewer.tsx";
import DocumentsViewerLegacy from "../../components/profile/DocumentsViewerLegacy.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import DocumentsReviewTable from "../../components/documents/DocumentsReviewTable.tsx";
import {
  getPendingDocumentsForReview,
  reviewPendingDocument,
} from "../../services/proceedings.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { getProfileImage } from "../../services/profileImageCache.ts";
import S3Service from "../../services/s3.service.ts";

type PendingDocument = {
  id: number;
  documentName: string;
  source: string;
  requestType?: "upload" | "deletion";
  partnerRequestComment?: string | null;
  partner: {
    id: number;
    name: string;
    branch?: string | null;
    position?: string | null;
    curp?: string | null;
  };
  file: {
    id: string;
    extension?: string;
    name?: string;
  };
  fileUrl?: string;
  metadata?: Record<string, any>;
  queuedAt?: string;
  createdAt?: string;
};

const PAGE_LIMIT = 1;
const TABLE_LIMIT = 10;

const DocumentsReview: React.FC = () => {
  const [pendingDoc, setPendingDoc] = useState<PendingDocument | null>(null);
  const [pendingDocs, setPendingDocs] = useState<PendingDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [decisionLoading, setDecisionLoading] = useState<
    "approved" | "rejected" | null
  >(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<PendingDocument | null>(null);
  const [previewProfileImageUrl, setPreviewProfileImageUrl] =
    useState<string>("");
  const [previewImageLoading, setPreviewImageLoading] = useState(true);
  const [previewImageError, setPreviewImageError] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState<string>("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [queueType, setQueueType] = useState<"upload" | "deletion">("upload");
  const [viewMode, setViewMode] = useState<"swipe" | "table">("swipe");
  const [viewingDocId, setViewingDocId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [currentPage, setCurrentPage] = useState(1);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  useAppSettings();

  const normalizeText = (text: string): string => {
    return text
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchInput.trim() === "") {
      setSearch("");
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      const normalized = normalizeText(searchInput);
      setSearch(normalized);

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    if (viewMode === "table" && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [viewMode]);

  const incrementReviewedToday = useCallback(() => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem("reviewedToday");
    let count = 0;
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.date === today) {
        count = parsed.count;
      }
    }
    localStorage.setItem(
      "reviewedToday",
      JSON.stringify({ date: today, count: count + 1 })
    );
  }, []);

  const fetchNextDocument = useCallback(async () => {
    setLoading(true);
    try {
      const desiredRequestType =
        queueType === "deletion" ? "deletion" : undefined;
      const response = await getPendingDocumentsForReview({
        limit: PAGE_LIMIT,
        offset: 0,
        requestType: desiredRequestType,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      const doc = rows[0] || null;
      setPendingDoc(doc);
      setTotal(response?.total || rows.length);
      setRejectComment("");

      if (doc?.partner?.id) {
        setImageLoading(true);
        setImageError(false);
        try {
          const profileUrl = await getProfileImage(doc.partner.id);
          if (profileUrl) {
            setProfileImageUrl(profileUrl);
          } else {
            setImageError(true);
            setProfileImageUrl("");
          }
        } catch (error) {
          setImageError(true);
          setProfileImageUrl("");
        } finally {
          setImageLoading(false);
        }
      } else {
        setProfileImageUrl("");
        setImageError(true);
        setImageLoading(false);
      }
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message ||
          "No se pudieron cargar los documentos pendientes",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  }, [queueType]);

  const fetchAllDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const desiredRequestType =
        queueType === "deletion" ? "deletion" : undefined;
      const offset = (currentPage - 1) * TABLE_LIMIT;
      const response = await getPendingDocumentsForReview({
        limit: TABLE_LIMIT,
        offset: offset,
        requestType: desiredRequestType,
        search: search || undefined,
        sort: sortBy,
        order: sortOrder,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setPendingDocs(rows);
      setTotal(response?.total || rows.length);
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message ||
          "No se pudieron cargar los documentos pendientes",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  }, [queueType, search, sortBy, sortOrder, currentPage]);

  const loadDocumentForView = useCallback(async (doc: PendingDocument) => {
    setPendingDoc(doc);
    setViewingDocId(doc.id);
    setRejectComment("");

    if (doc?.partner?.id) {
      setImageLoading(true);
      setImageError(false);
      try {
        const profileUrl = await getProfileImage(doc.partner.id);
        if (profileUrl) {
          setProfileImageUrl(profileUrl);
        } else {
          setImageError(true);
          setProfileImageUrl("");
        }
      } catch (error) {
        setImageError(true);
        setProfileImageUrl("");
      } finally {
        setImageLoading(false);
      }
    } else {
      setProfileImageUrl("");
      setImageError(true);
      setImageLoading(false);
    }
  }, []);

  const openPreviewModal = useCallback(async (doc: PendingDocument) => {
    setPreviewDoc(doc);
    setPreviewModalOpen(true);
    setRejectComment("");

    if (doc?.partner?.id) {
      setPreviewImageLoading(true);
      setPreviewImageError(false);
      try {
        const profileUrl = await getProfileImage(doc.partner.id);
        if (profileUrl) {
          setPreviewProfileImageUrl(profileUrl);
        } else {
          setPreviewImageError(true);
          setPreviewProfileImageUrl("");
        }
      } catch (error) {
        setPreviewImageError(true);
        setPreviewProfileImageUrl("");
      } finally {
        setPreviewImageLoading(false);
      }
    } else {
      setPreviewProfileImageUrl("");
      setPreviewImageError(true);
      setPreviewImageLoading(false);
    }
  }, []);

  const handleModalDecision = async (
    status: "approved" | "rejected",
    rejectionNote?: string
  ) => {
    if (!previewDoc) return;

    if (
      (queueType === "upload" || queueType === "deletion") &&
      status === "rejected" &&
      !rejectionNote?.trim()
    ) {
      notification(
        "Comentario requerido",
        "Debes proporcionar un motivo para rechazar la solicitud",
        "warning"
      );
      return;
    }

    setDecisionLoading(status);
    try {
      await reviewPendingDocument(previewDoc.id, {
        status,
        comment: rejectionNote?.trim() || undefined,
      });
      incrementReviewedToday();
      notification(
        "Documento revisado",
        `El documento ha sido ${
          status === "approved" ? "aprobado" : "rechazado"
        } exitosamente`,
        "success"
      );
      setRejectComment("");
      setRejectModalOpen(false);
      setPreviewModalOpen(false);
      setPreviewDoc(null);
      await fetchAllDocuments();
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al revisar el documento",
        "danger"
      );
    } finally {
      setDecisionLoading(null);
    }
  };

  const handleModalApprove = () => handleModalDecision("approved");
  const handleModalReject = () => {
    if (queueType === "upload" || queueType === "deletion") {
      setRejectModalOpen(true);
    } else {
      handleModalDecision("rejected");
    }
  };

  useEffect(() => {
    if (viewMode === "swipe") {
      fetchNextDocument();
    } else {
      setCurrentPage(1);
      fetchAllDocuments();
    }
  }, [viewMode, queueType, fetchNextDocument]);

  useEffect(() => {
    if (viewMode === "table") {
      setCurrentPage(1);
    }
  }, [search, sortBy, sortOrder]);

  useEffect(() => {
    if (viewMode === "table") {
      fetchAllDocuments();
    }
  }, [
    viewMode,
    queueType,
    search,
    sortBy,
    sortOrder,
    currentPage,
    fetchAllDocuments,
  ]);

  const handleDecision = async (
    status: "approved" | "rejected",
    rejectionNote?: string
  ) => {
    if (!pendingDoc) return;

    if (
      (queueType === "upload" || queueType === "deletion") &&
      status === "rejected" &&
      !rejectionNote?.trim()
    ) {
      notification(
        "Comentario requerido",
        "Debes proporcionar un motivo para rechazar la solicitud",
        "warning"
      );
      return;
    }

    setDecisionLoading(status);
    try {
      await reviewPendingDocument(pendingDoc.id, {
        status,
        comment: rejectionNote?.trim() || undefined,
      });
      incrementReviewedToday();
      notification(
        "Documento revisado",
        `El documento ha sido ${
          status === "approved" ? "aprobado" : "rechazado"
        } exitosamente`,
        "success"
      );
      setRejectComment("");
      setRejectModalOpen(false);
      if (viewMode === "swipe") {
        await fetchNextDocument();
      } else {
        setViewMode("table");
        setViewingDocId(null);
        await fetchAllDocuments();
      }
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al revisar el documento",
        "danger"
      );
    } finally {
      setDecisionLoading(null);
    }
  };

  const getExtensionFromUrl = (url: string | undefined): string | null => {
    if (!url) return null;
    try {
      const patterns = [
        /filename(?:%3D|=)(?:%22|")([^"%]+)(?:%22|")/i,
        /filename%3D%22([^"%]+)%22/i,
        /filename="([^"]+)"/i,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          const decoded = decodeURIComponent(match[1]);
          const parts = decoded.split(".");
          if (parts.length > 1) {
            const ext = parts[parts.length - 1].toLowerCase().trim();
            if (ext) return ext;
          }
        }
      }

      try {
        const asUrl = new URL(url);
        const rcd = asUrl.searchParams.get("response-content-disposition");
        if (rcd) {
          const decoded = decodeURIComponent(rcd);
          const nameMatch = decoded.match(/filename="?([^";]+)"?/i);
          if (nameMatch && nameMatch[1]) {
            const parts = nameMatch[1].split(".");
            if (parts.length > 1) {
              const ext = parts[parts.length - 1].toLowerCase().trim();
              if (ext) return ext;
            }
          }
        }
      } catch (e) {}

      const pathPartMatch = url.match(/\.([a-zA-Z0-9]+)(?:\?|&|$)/);
      if (pathPartMatch && pathPartMatch[1])
        return pathPartMatch[1].toLowerCase();

      if (/pdf/i.test(url)) return "pdf";
      if (/jpe?g/i.test(url)) return "jpg";
      if (/png/i.test(url)) return "png";
    } catch (error) {
      console.error("Error extracting extension from URL:", error);
    }
    return null;
  };

  const [resolvedLegacyPreview, setResolvedLegacyPreview] = useState<{
    docId: number;
    url: string;
    extension: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!pendingDoc || pendingDoc.source !== "legacy") {
        setResolvedLegacyPreview(null);
        return;
      }

      const rawUrl = pendingDoc.fileUrl;
      const extFromUrl = getExtensionFromUrl(rawUrl);

      if (rawUrl && extFromUrl) {
        setResolvedLegacyPreview({
          docId: pendingDoc.id,
          url: rawUrl,
          extension: extFromUrl,
        });
        return;
      }

      const key = pendingDoc?.file?.id || pendingDoc?.file?.name;
      if (!key) {
        setResolvedLegacyPreview(null);
        return;
      }

      try {
        const urls = await S3Service.getFiles(key);
        const first = Array.isArray(urls) ? urls[0]?.url : null;
        if (!first) {
          setResolvedLegacyPreview(null);
          return;
        }

        const ext = getExtensionFromUrl(first);
        if (!cancelled) {
          setResolvedLegacyPreview({
            docId: pendingDoc.id,
            url: first,
            extension: ext,
          });
        }
      } catch (e) {
        if (!cancelled) setResolvedLegacyPreview(null);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [
    pendingDoc?.id,
    pendingDoc?.source,
    pendingDoc?.fileUrl,
    pendingDoc?.file?.id,
  ]);

  const viewerData = useMemo(() => {
    if (!pendingDoc?.file?.id || !pendingDoc?.fileUrl) return [];

    const resolvedUrl =
      pendingDoc.source === "legacy" &&
      resolvedLegacyPreview?.docId === pendingDoc.id &&
      resolvedLegacyPreview?.url
        ? resolvedLegacyPreview.url
        : pendingDoc.fileUrl;

    const resolvedExt =
      pendingDoc.source === "legacy" &&
      resolvedLegacyPreview?.docId === pendingDoc.id
        ? resolvedLegacyPreview.extension
        : null;

    const extension =
      resolvedExt ||
      getExtensionFromUrl(resolvedUrl) ||
      pendingDoc.file?.extension ||
      "pdf";

    return [
      {
        file: {
          id: pendingDoc.file.id,
          extension: extension,
          name: pendingDoc.file?.name || pendingDoc.documentName || "documento",
        },
        url: resolvedUrl,
      },
    ];
  }, [pendingDoc, resolvedLegacyPreview]);

  const isLegacyDoc = useMemo(() => {
    return pendingDoc?.source === "legacy";
  }, [pendingDoc]);

  const previewViewerData = useMemo(() => {
    if (!previewDoc?.file?.id || !previewDoc?.fileUrl) return [];

    const extension =
      previewDoc.file?.extension ||
      getExtensionFromUrl(previewDoc.fileUrl) ||
      "pdf";

    return [
      {
        file: {
          id: previewDoc.file.id,
          extension: extension,
          name: previewDoc.file?.name || previewDoc.documentName || "documento",
        },
        url: previewDoc.fileUrl,
      },
    ];
  }, [previewDoc]);

  const isPreviewLegacy = useMemo(() => {
    return previewDoc?.source === "legacy";
  }, [previewDoc]);

  const handleApprove = () => handleDecision("approved");

  const openRejectModal = () => {
    setRejectComment("");
    setRejectModalOpen(true);
  };

  const confirmReject = () => {
    if (previewDoc) {
      handleModalDecision("rejected", rejectComment);
    } else {
      handleDecision("rejected", rejectComment);
    }
  };

  const handleDragStart = (clientX: number) => {
    if (decisionLoading) return;
    setIsDragging(true);
    setStartX(clientX);
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging || decisionLoading) return;
    setDragOffset(clientX - startX);
  };

  const handleDragEnd = () => {
    if (!isDragging || decisionLoading) return;
    const threshold = 150;
    if (dragOffset > threshold) {
      handleApprove();
    } else if (dragOffset < -threshold) {
      openRejectModal();
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const dragProgress = Math.min(Math.abs(dragOffset) / 150, 1);

  const isDeletionQueue = queueType === "deletion";
  const leftActionLabel = isDeletionQueue ? "Rechazar solicitud" : "Rechazar";
  const rightActionLabel = isDeletionQueue
    ? "Aprobar solicitud de rechazo"
    : "Aprobar";
  const leftOverlayText = isDeletionQueue ? "RECHAZAR SOLICITUD" : "RECHAZAR";
  const rightOverlayText = isDeletionQueue ? "APROBAR SOLICITUD" : "APROBAR";

  return (
    <Layout loading={loading}>
      <div
        className="d-flex flex-column align-items-center justify-content-start pt-4 position-relative"
        style={{
          minHeight: "calc(100vh - 120px)",
          padding: "24px",
          background: "#f8f9fa",
        }}
      >
        <button
          className="position-absolute border-0"
          style={{
            top: "24px",
            right: "24px",
            background: "#ffffff",
            color: "#202124",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onClick={() => {
            if (viewMode === "swipe") {
              setViewMode("table");
              setViewingDocId(null);
            } else {
              setViewMode("swipe");
              setViewingDocId(null);
            }
          }}
        >
          <i
            className={`fa fa-${
              viewMode === "swipe" ? "table" : "hand-pointer"
            }`}
          />
          {viewMode === "swipe" ? "Vista tabla" : "Vista swipe"}
        </button>
        <div
          className="d-flex justify-content-center align-items-center flex-wrap mb-4"
          style={{ gap: "0.75rem" }}
        >
          {[
            { key: "upload", label: "Documentos por aprobar" },
            { key: "deletion", label: "Solicitudes de rechazo" },
          ].map((option) => {
            const active = queueType === option.key;
            return (
              <button
                key={option.key}
                className="border-0 text-sm fw-semibold transition-all shadow-sm"
                style={{
                  background: active
                    ? "linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #dc2626 100%)"
                    : "linear-gradient(135deg, #fef3c7 0%, #fed7aa 50%, #fee2e2 100%)",
                  color: active ? "#ffffff" : "#374151",
                  borderRadius: "16px",
                  padding: "0.75rem 1.5rem",
                  minWidth: "200px",
                  transform: active ? "scale(1.05)" : "scale(1)",
                  boxShadow: active
                    ? "0 4px 12px rgba(251, 191, 36, 0.4), 0 2px 4px rgba(220, 38, 38, 0.2)"
                    : "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
                onClick={() => setQueueType(option.key as any)}
                disabled={active}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {viewMode === "table" && !viewingDocId ? (
          <DocumentsReviewTable
            pendingDocs={pendingDocs}
            loading={loading}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            setSearch={setSearch}
            searchInputRef={searchInputRef}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={total}
            queueType={queueType}
            onPreview={openPreviewModal}
            viewMode={viewMode}
          />
        ) : (
          <>
            {loading ? (
              <div
                className="bg-white rounded-4 p-5 text-center"
                style={{
                  maxWidth: "520px",
                  width: "100%",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                }}
              >
                <Loader isLoading />
              </div>
            ) : !pendingDoc ? (
              <div
                className="bg-white rounded-4 p-5 text-center"
                style={{
                  maxWidth: "420px",
                  width: "100%",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "72px",
                    height: "72px",
                    background: "#f1f3f4",
                  }}
                >
                  <i
                    className="fa fa-check-circle"
                    style={{ fontSize: "32px", color: "#5f6368" }}
                  />
                </div>
                <h4 className="fw-semibold mb-2" style={{ color: "#202124" }}>
                  {queueType === "deletion" ? "Sin solicitudes" : "Todo listo"}
                </h4>
                <p
                  className="mb-4"
                  style={{ color: "#5f6368", fontSize: "14px" }}
                >
                  {queueType === "deletion"
                    ? "No hay solicitudes de rechazo pendientes"
                    : "No hay documentos pendientes por revisar"}
                </p>
                <button
                  className="btn px-4 py-2"
                  style={{
                    background: "#f1f3f4",
                    color: "#1a73e8",
                    fontWeight: 500,
                    fontSize: "14px",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  onClick={fetchNextDocument}
                >
                  <i className="fa fa-refresh me-2" />
                  Verificar de nuevo
                </button>
              </div>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center gap-4 w-100"
                style={{ maxWidth: "1100px" }}
              >
                <div
                  className="d-none d-lg-flex flex-column align-items-center justify-content-center text-center"
                  style={{
                    width: "140px",
                    opacity: dragOffset < 0 ? 0.3 + dragProgress * 0.7 : 0.3,
                    transform: `scale(${
                      dragOffset < 0 ? 1 + dragProgress * 0.1 : 1
                    })`,
                    transition: isDragging ? "none" : "all 0.25s ease",
                  }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "64px",
                      height: "64px",
                      background:
                        dragOffset < 0 ? "rgba(220, 53, 69, 0.15)" : "#f1f3f4",
                      transition: isDragging ? "none" : "background 0.25s ease",
                    }}
                  >
                    <i
                      className="fa fa-arrow-left"
                      style={{
                        fontSize: "24px",
                        color: dragOffset < 0 ? "#dc3545" : "#80868b",
                        transition: isDragging ? "none" : "color 0.25s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: dragOffset < 0 ? "#dc3545" : "#80868b",
                      transition: isDragging ? "none" : "color 0.25s ease",
                    }}
                  >
                    {leftActionLabel}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#9ca3af",
                      marginTop: "4px",
                    }}
                  >
                    Desliza a la izquierda
                  </div>
                </div>

                <div
                  className="bg-white rounded-4 overflow-hidden position-relative"
                  style={{
                    maxWidth: "720px",
                    width: "100%",
                    boxShadow: isDragging
                      ? "0 16px 48px rgba(0,0,0,0.15)"
                      : "0 8px 32px rgba(0,0,0,0.08)",
                    cursor: isDragging ? "grabbing" : "grab",
                    transform: `translateX(${dragOffset}px) rotate(${
                      dragOffset * 0.012
                    }deg)`,
                    transition: isDragging ? "none" : "all 0.3s ease-out",
                    userSelect: "none",
                  }}
                  onMouseDown={(e) => handleDragStart(e.clientX)}
                  onMouseMove={(e) => handleDragMove(e.clientX)}
                  onMouseUp={handleDragEnd}
                  onMouseLeave={handleDragEnd}
                  onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
                  onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
                  onTouchEnd={handleDragEnd}
                >
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center rounded-4"
                    style={{
                      inset: 0,
                      background: "rgba(220, 53, 69, 0.92)",
                      opacity: dragOffset < 0 ? dragProgress : 0,
                      pointerEvents: "none",
                      zIndex: 20,
                      transition: isDragging ? "none" : "opacity 0.25s ease",
                    }}
                  >
                    <div
                      className="text-white text-center"
                      style={{ transform: "rotate(-8deg)" }}
                    >
                      <i
                        className="fa fa-times-circle mb-2"
                        style={{ fontSize: "56px" }}
                      />
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 700,
                          letterSpacing: "2px",
                        }}
                      >
                        {leftOverlayText}
                      </div>
                    </div>
                  </div>

                  <div
                    className="position-absolute d-flex align-items-center justify-content-center rounded-4"
                    style={{
                      inset: 0,
                      background: "rgba(52, 168, 83, 0.92)",
                      opacity: dragOffset > 0 ? dragProgress : 0,
                      pointerEvents: "none",
                      zIndex: 20,
                      transition: isDragging ? "none" : "opacity 0.25s ease",
                    }}
                  >
                    <div
                      className="text-white text-center"
                      style={{ transform: "rotate(8deg)" }}
                    >
                      <i
                        className="fa fa-check-circle mb-2"
                        style={{ fontSize: "56px" }}
                      />
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 700,
                          letterSpacing: "2px",
                        }}
                      >
                        {rightOverlayText}
                      </div>
                    </div>
                  </div>

                  <div
                    className="px-4 py-3 d-flex align-items-center gap-3"
                    style={{ borderBottom: "1px solid #e8eaed" }}
                  >
                    <div className="flex-shrink-0">
                      {imageLoading || imageError || !profileImageUrl ? (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "44px",
                            height: "44px",
                            background: "#e8eaed",
                          }}
                        >
                          <i
                            className="fa fa-user"
                            style={{ fontSize: "18px", color: "#80868b" }}
                          />
                        </div>
                      ) : (
                        <img
                          src={profileImageUrl}
                          alt=""
                          onError={() => {
                            setImageError(true);
                            setProfileImageUrl("");
                          }}
                          className="rounded-circle"
                          style={{
                            width: "44px",
                            height: "44px",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </div>

                    <div className="flex-grow-1 min-w-0">
                      <div
                        className="fw-semibold text-truncate"
                        style={{ fontSize: "15px", color: "#202124" }}
                      >
                        {pendingDoc.partner?.name || "Sin nombre"}
                      </div>
                      <div
                        className="text-truncate"
                        style={{ fontSize: "12px", color: "#5f6368" }}
                      >
                        {[
                          pendingDoc.partner?.position,
                          pendingDoc.partner?.branch,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>

                    <div
                      className="flex-shrink-0 px-3 py-1 rounded-pill"
                      style={{
                        background: "#e8f0fe",
                        color: "#1967d2",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {total}{" "}
                      {queueType === "deletion"
                        ? `solicitud${total !== 1 ? "es" : ""}`
                        : `pendiente${total !== 1 ? "s" : ""}`}
                    </div>
                  </div>

                  <div
                    className="px-4 py-3 d-flex gap-4"
                    style={{
                      background: "#fafbfc",
                      borderBottom: "1px solid #e8eaed",
                    }}
                  >
                    <div className="flex-grow-1">
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#80868b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "2px",
                        }}
                      >
                        Documento
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="fw-medium"
                          style={{ fontSize: "13px", color: "#202124" }}
                        >
                          {pendingDoc.documentName}
                        </div>
                        {isLegacyDoc && (
                          <span
                            className="badge"
                            style={{
                              background: "#e8f0fe",
                              color: "#1967d2",
                              fontSize: "10px",
                              fontWeight: 500,
                              padding: "2px 8px",
                            }}
                          >
                            Legacy
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#80868b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "2px",
                        }}
                      >
                        CURP
                      </div>
                      <div
                        className="fw-medium"
                        style={{
                          fontSize: "13px",
                          color: "#202124",
                          fontFamily: "monospace",
                        }}
                      >
                        {pendingDoc.partner?.curp || "—"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#80868b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "2px",
                        }}
                      >
                        Fecha
                      </div>
                      <div
                        className="fw-medium"
                        style={{ fontSize: "13px", color: "#202124" }}
                      >
                        {formatDate(
                          pendingDoc.metadata?.uploadedAt || pendingDoc.queuedAt
                        )}
                      </div>
                    </div>
                  </div>

                  {isDeletionQueue && (
                    <div
                      className="px-4 py-3"
                      style={{
                        background: "#fff4e5",
                        borderBottom: "1px solid #e8eaed",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#b45309",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "2px",
                        }}
                      >
                        Motivo
                      </div>
                      <div
                        className="fw-medium"
                        style={{ fontSize: "13px", color: "#78350f" }}
                      >
                        {pendingDoc.partnerRequestComment?.trim()
                          ? pendingDoc.partnerRequestComment
                          : "El colaborador solicitó eliminar este documento para volver a subirlo."}
                      </div>
                    </div>
                  )}

                  <div
                    className="p-4"
                    style={{
                      height: "600px",
                      position: "relative",
                    }}
                  >
                    <div
                      className="h-100 rounded-3 overflow-hidden"
                      style={{
                        background: "#f1f3f4",
                        position: "relative",
                      }}
                    >
                      {viewerData.length > 0 ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            overflow: "auto",
                          }}
                        >
                          <div style={{ width: "100%", height: "100%" }}>
                            {isLegacyDoc ? (
                              <DocumentsViewerLegacy fileData={viewerData} />
                            ) : (
                              <DocumentsViewer fileData={viewerData} />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                          <i
                            className="fa fa-file-pdf mb-3"
                            style={{ fontSize: "48px", color: "#80868b" }}
                          />
                          <span style={{ color: "#5f6368", fontSize: "14px" }}>
                            Vista previa no disponible
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="d-none d-lg-flex flex-column align-items-center justify-content-center text-center"
                  style={{
                    width: "140px",
                    opacity: dragOffset > 0 ? 0.3 + dragProgress * 0.7 : 0.3,
                    transform: `scale(${
                      dragOffset > 0 ? 1 + dragProgress * 0.1 : 1
                    })`,
                    transition: isDragging ? "none" : "all 0.25s ease",
                  }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "64px",
                      height: "64px",
                      background:
                        dragOffset > 0 ? "rgba(52, 168, 83, 0.15)" : "#f1f3f4",
                      transition: isDragging ? "none" : "background 0.25s ease",
                    }}
                  >
                    <i
                      className="fa fa-arrow-right"
                      style={{
                        fontSize: "24px",
                        color: dragOffset > 0 ? "#34a853" : "#80868b",
                        transition: isDragging ? "none" : "color 0.25s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: dragOffset > 0 ? "#34a853" : "#80868b",
                      transition: isDragging ? "none" : "color 0.25s ease",
                    }}
                  >
                    {rightActionLabel}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#9ca3af",
                      marginTop: "4px",
                    }}
                  >
                    Desliza a la derecha
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ModalContainer
        open={previewModalOpen}
        title="Vista previa del documento"
        closeFn={() => {
          setPreviewModalOpen(false);
          setPreviewDoc(null);
          setRejectComment("");
        }}
        size="xl"
      >
        {previewDoc && (
          <div>
            <div
              className="px-4 py-3 d-flex align-items-center gap-3 mb-3"
              style={{ borderBottom: "1px solid #e8eaed" }}
            >
              <div className="flex-shrink-0">
                {previewImageLoading ||
                previewImageError ||
                !previewProfileImageUrl ? (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "44px",
                      height: "44px",
                      background: "#e8eaed",
                    }}
                  >
                    <i
                      className="fa fa-user"
                      style={{ fontSize: "18px", color: "#80868b" }}
                    />
                  </div>
                ) : (
                  <img
                    src={previewProfileImageUrl}
                    alt=""
                    onError={() => {
                      setPreviewImageError(true);
                      setPreviewProfileImageUrl("");
                    }}
                    className="rounded-circle"
                    style={{
                      width: "44px",
                      height: "44px",
                      objectFit: "cover",
                    }}
                  />
                )}
              </div>
              <div className="flex-grow-1 min-w-0">
                <div
                  className="fw-semibold text-truncate"
                  style={{ fontSize: "15px", color: "#202124" }}
                >
                  {previewDoc.partner?.name || "Sin nombre"}
                </div>
                <div
                  className="text-truncate"
                  style={{ fontSize: "12px", color: "#5f6368" }}
                >
                  {[previewDoc.partner?.position, previewDoc.partner?.branch]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>

            <div
              className="px-4 py-3 d-flex gap-4 mb-3"
              style={{
                background: "#fafbfc",
                borderBottom: "1px solid #e8eaed",
              }}
            >
              <div className="flex-grow-1">
                <div
                  style={{
                    fontSize: "10px",
                    color: "#80868b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  Documento
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="fw-medium"
                    style={{ fontSize: "13px", color: "#202124" }}
                  >
                    {previewDoc.documentName}
                  </div>
                  {/* {isPreviewLegacy && (
                    <span
                      className="badge"
                      style={{
                        background: "#e8f0fe",
                        color: "#1967d2",
                        fontSize: "10px",
                        fontWeight: 500,
                        padding: "2px 8px",
                      }}
                    >
                      
                    </span>
                  )} */}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#80868b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  CURP
                </div>
                <div
                  className="fw-medium"
                  style={{
                    fontSize: "13px",
                    color: "#202124",
                    fontFamily: "monospace",
                  }}
                >
                  {previewDoc.partner?.curp || "—"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#80868b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  Fecha
                </div>
                <div
                  className="fw-medium"
                  style={{ fontSize: "13px", color: "#202124" }}
                >
                  {formatDate(previewDoc.createdAt || previewDoc.queuedAt)}
                </div>
              </div>
            </div>

            {isDeletionQueue && (
              <div
                className="px-4 py-3 mb-3"
                style={{
                  background: "#fff4e5",
                  borderBottom: "1px solid #e8eaed",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: "#b45309",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  Motivo
                </div>
                <div
                  className="fw-medium"
                  style={{ fontSize: "13px", color: "#78350f" }}
                >
                  {previewDoc.partnerRequestComment?.trim()
                    ? previewDoc.partnerRequestComment
                    : "El colaborador solicitó eliminar este documento para volver a subirlo."}
                </div>
              </div>
            )}

            <div
              className="mb-4"
              style={{
                height: "500px",
                position: "relative",
                border: "1px solid #e8eaed",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <div
                className="h-100 rounded-3 overflow-hidden"
                style={{
                  background: "#f1f3f4",
                  position: "relative",
                }}
              >
                {previewViewerData.length > 0 ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    <div style={{ width: "100%", height: "100%" }}>
                      {isPreviewLegacy ? (
                        <DocumentsViewerLegacy fileData={previewViewerData} />
                      ) : (
                        <DocumentsViewer fileData={previewViewerData} />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <i
                      className="fa fa-file-pdf mb-3"
                      style={{ fontSize: "48px", color: "#80868b" }}
                    />
                    <span style={{ color: "#5f6368", fontSize: "14px" }}>
                      Vista previa no disponible
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 pt-3 border-top">
              <button
                className="btn px-4 py-2"
                style={{
                  background: "#f1f3f4",
                  color: "#5f6368",
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "8px",
                }}
                onClick={() => {
                  setPreviewModalOpen(false);
                  setPreviewDoc(null);
                  setRejectComment("");
                }}
                disabled={decisionLoading !== null}
              >
                Cerrar
              </button>
              <button
                className="btn px-4 py-2"
                style={{
                  background: "#dc3545",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "8px",
                }}
                onClick={handleModalReject}
                disabled={decisionLoading !== null}
              >
                {decisionLoading === "rejected" ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="fa fa-times me-2" />
                    {isDeletionQueue ? "Rechazar solicitud" : "Rechazar"}
                  </>
                )}
              </button>
              <button
                className="btn px-4 py-2"
                style={{
                  background: "#34a853",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "8px",
                }}
                onClick={handleModalApprove}
                disabled={decisionLoading !== null}
              >
                {decisionLoading === "approved" ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check me-2" />
                    {isDeletionQueue
                      ? "Aprobar solicitud de rechazo"
                      : "Aprobar"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </ModalContainer>

      <ModalContainer
        open={rejectModalOpen}
        title={
          isDeletionQueue
            ? "Motivo del rechazo de la solicitud"
            : "Motivo del rechazo"
        }
        closeFn={() => {
          setRejectModalOpen(false);
          setRejectComment("");
        }}
      >
        <div className="mb-4">
          <label
            className="form-label"
            style={{ fontSize: "14px", color: "#202124", fontWeight: 500 }}
          >
            {isDeletionQueue
              ? "¿Por qué se rechaza esta solicitud de eliminación?"
              : "¿Por qué se rechaza este documento?"}
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder={
              isDeletionQueue
                ? "Ej: El documento es válido y no debe eliminarse, etc."
                : "Ej: Documento ilegible, información incompleta, etc."
            }
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            style={{
              fontSize: "14px",
              border: "1px solid #dadce0",
              borderRadius: "8px",
              resize: "none",
            }}
          />
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn px-4 py-2"
            style={{
              background: "#f1f3f4",
              color: "#5f6368",
              fontWeight: 500,
              fontSize: "14px",
              border: "none",
              borderRadius: "8px",
            }}
            onClick={() => setRejectModalOpen(false)}
            disabled={decisionLoading === "rejected"}
          >
            Cancelar
          </button>
          <button
            className="btn px-4 py-2"
            style={{
              background: "#d93025",
              color: "white",
              fontWeight: 500,
              fontSize: "14px",
              border: "none",
              borderRadius: "8px",
            }}
            onClick={confirmReject}
            disabled={decisionLoading === "rejected"}
          >
            {decisionLoading === "rejected" ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Procesando...
              </>
            ) : isDeletionQueue ? (
              "Confirmar rechazo de solicitud"
            ) : (
              "Confirmar rechazo"
            )}
          </button>
        </div>
      </ModalContainer>
    </Layout>
  );
};

export default DocumentsReview;
